import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import IconEdit from '@/icons/Edit.svg';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { QualityControlMessage } from './shared/QualityControlMessage';
import { BaseMessage } from './BaseMessage';
import ManuallyUpdateStatusDueAtDialog from './ManuallyUpdateStatusDueAtDialog';

export const RepairArticleTaskMessage = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();
  const { workflow } = useRequestContext();
  const [isOpen, setIsOpen] = useState(false);

  const isLastQualityControlKO = article.steps?.findLast(
    (step) => step.status === 'cancelled' && step.step === 'quality-control'
  );

  const repairStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'repair');
  const canEditStatusDueAt =
    article.step?.step === 'repair' ? repairStep?.config?.editRepairDueDays?.enabled : false;

  const EditButton = canEditStatusDueAt ? (
    <Button
      variant="secondary"
      size="small"
      iconOnly
      tooltip={t({ id: 'article.step-task-message.edit', message: 'Edit repair completion date' })}
      ariaLabel={t({
        id: 'article.step-task-message.edit',
        message: 'Edit repair completion date',
      })}
      onPress={() => setIsOpen(true)}
    >
      <IconEdit />
    </Button>
  ) : undefined;

  if (article.workshop?.workshopConfig?.nonDigitalized) {
    return (
      <>
        <BaseMessage
          title={
            <Trans id="article.task.repair-article.on-behalf.title">Workshop repair required</Trans>
          }
          message={
            <Trans id="article.task.repair-article.on-behalf.message">
              Please contact the workshop to validate the repair job on its behalf.
            </Trans>
          }
          article={article}
          titleAction={EditButton}
        />
        <ManuallyUpdateStatusDueAtDialog article={article} isOpen={isOpen} setIsOpen={setIsOpen} />
      </>
    );
  }

  return (
    <>
      <BaseMessage
        title={<Trans id="article.task.repair-article.title">Repair the item</Trans>}
        message={
          <Stack gap="0.25rem">
            <p>
              <Trans id="article.task.repair-article.message">
                Proceed with the item's repair. Once completed, click the &quot;Mark as
                Repaired&quot; button below.
              </Trans>
            </p>
            {isLastQualityControlKO && <QualityControlMessage article={article} />}
          </Stack>
        }
        article={article}
        titleAction={EditButton}
      />
      <ManuallyUpdateStatusDueAtDialog article={article} isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
};
